/**
 * Sprint 233 — Checkout success / status polling page.
 * Reached via Stripe success_url. Polls /packages/checkout-status/:session_id
 * until payment is paid or expired, then displays the order summary.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight, Sparkles } from 'lucide-react';

const PackageCheckoutSuccess = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling');  // polling | paid | expired | error
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await api.get(`/packages/checkout-status/${sessionId}`);
        const ps = res.data?.payment_status;
        const ss = res.data?.stripe_status;
        if (!cancelled) setOrder(res.data?.order);
        if (ps === 'paid') { if (!cancelled) setStatus('paid'); return; }
        if (ss === 'expired') { if (!cancelled) setStatus('expired'); return; }
        if (attempts >= 8) { if (!cancelled) setStatus('error'); return; }
        setTimeout(tick, 2000);
      } catch {
        if (!cancelled) setStatus('error');
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0B0F14] grid place-items-center text-white px-4" data-testid="checkout-success">
      <div className="max-w-md w-full rounded-2xl bg-white/[0.05] border border-white/[0.08] p-8 text-center">
        {status === 'polling' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[#0DEAE3] animate-spin" />
            <h2 className="text-xl font-bold mt-4">Confirming your payment…</h2>
            <p className="text-sm text-white/55 mt-1">Just a moment while Stripe finalizes the charge.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-400" />
            <h2 className="text-2xl font-bold mt-4">Payment received 🎉</h2>
            <p className="text-sm text-white/65 mt-1">
              {order?.package?.name ? `Your ${order.package.name} order is queued — we’ll assign a creator and reach out shortly.`
                                     : 'Your order is queued — we will reach out shortly.'}
            </p>
            <div className="grid grid-cols-2 gap-2 mt-6">
              <Button onClick={() => navigate('/client/packages/orders')}
                      className="bg-[#0DEAE3] text-[#0B0F14] hover:bg-[#0DEAE3]/90 font-semibold" data-testid="view-orders-btn">
                My Orders <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/client/dashboard')}
                      className="border-white/15 bg-white/5 text-white hover:bg-white/10">
                Dashboard
              </Button>
            </div>
          </>
        )}
        {status === 'expired' && (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-400" />
            <h2 className="text-xl font-bold mt-4">Checkout expired</h2>
            <p className="text-sm text-white/55 mt-1">No charge was made. Feel free to try again.</p>
            <Button onClick={() => navigate('/client/packages')}
                    className="mt-5 bg-[#0DEAE3] text-[#0B0F14] hover:bg-[#0DEAE3]/90 font-semibold">
              Back to Packages
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-rose-400" />
            <h2 className="text-xl font-bold mt-4">We hit a snag</h2>
            <p className="text-sm text-white/55 mt-1">If you completed payment we’ll still process it. Check your orders.</p>
            <Button onClick={() => navigate('/client/packages/orders')}
                    className="mt-5 bg-[#0DEAE3] text-[#0B0F14] hover:bg-[#0DEAE3]/90 font-semibold">
              View My Orders
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PackageCheckoutSuccess;
